import React, { useState } from 'react';
import { Tabs, Tab, Box, Typography, Button, TextField, Tooltip } from '@mui/material';
import SpinnerModal from './SpinnerModal';
import jsPDF from 'jspdf';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';


const TestData = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [testdataFileName, settestdataFileName] = useState('');
  const [testdataFile, settestdataFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [showSpinnerModal, setShowSpinnerModal] = useState(false);
  const [apiResult, setApiResult] = useState(null);
  const [specialInstruction, setSpecialInstruction] = useState('');
  const [isFinalSubmit, setIsFinalSubmit] = useState(false);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  // File upload handler
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      settestdataFileName(file.name);
      settestdataFile(file);

      // Upload the file to S3
      const formData = new FormData();
      formData.append('file', file);

      try {
        await fetch('https://api.elevate.de.ltim.io/modernization_task_file_upload/', {
          method: 'POST',
          body: formData,
        });
      } catch (error) {
        alert(`Error uploading ${file.name}: ${error.message}`);
      }
    }
  };

  // Download PDF handler
  const handleDownloadPDF = () => {
    if (!apiResult || !apiResult.report) {
      alert('No API result available to download.');
      return;
    }
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Output', 10, 10);
    const resultContent = String(apiResult.report);
    doc.setFontSize(11);
    const pageWidth = doc.internal.pageSize.width;
    const margin = 10;
    const contentWidth = pageWidth - margin * 2;
    const lines = doc.splitTextToSize(resultContent, contentWidth);
    let cursorY = 20;
    const lineHeight = 7;
    const pageHeight = doc.internal.pageSize.height;
    lines.forEach((line) => {
      if (cursorY + lineHeight > pageHeight - margin) {
        doc.addPage();
        cursorY = margin;
      }
      doc.text(line, margin, cursorY);
      cursorY += lineHeight;
    });
    doc.save('Output.pdf');
  };

  // Copy code handler
  const handleCopyCode = () => {
    if (!apiResult || !apiResult.report) {
      alert('No API result available to copy.');
      return;
    }
    const resultContent = String(apiResult.report);
    navigator.clipboard.writeText(resultContent).then(() => {
      //   alert('Result content copied to clipboard!');
    }).catch((error) => {
      console.error('Failed to copy text:', error);
    });
  };

  // Download Test Data handler
  const handleDownloadTestData = () => {
    if (!apiResult) {
      alert('No API result available.');
      return;
    }
    if (!Array.isArray(apiResult.links) || apiResult.links.length === 0) {
      alert('No test data links available to download.');
      return;
    }
    // Download each file sequentially
    const downloadFile = (index) => {
      if (index >= apiResult.links.length) return;
      const link = apiResult.links[index];
      if (typeof link === 'string' && link.trim() !== '') {
        const anchor = document.createElement('a');
        anchor.href = link;
        anchor.target = '_self';
        anchor.download = '';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        setTimeout(() => downloadFile(index + 1), 1000);
      } else {
        downloadFile(index + 1);
      }
    };
    downloadFile(0);
  };

  // Submit handler: send payload to /process-tasks/
  const handleSubmit = async () => {
    if (!testdataFileName) return;

    setIsFinalSubmit(true);
    setShowSpinnerModal(true);
    setProgress(0);

    // Simulate progress
    let interval=null;
    interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 10000);

    const payload = {
      file_name: testdataFileName,
      is_test_script: false,
      is_test_data: true,
      instruction: specialInstruction,
    };

    try {
      const response = await fetch('https://api.elevate.de.ltim.io/modernization_task/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      clearInterval(interval);
      setProgress(100);

      if (!response.ok) {
        const errorData = await response.json();
        alert(`Failed to process test script: ${errorData.detail}`);
        setShowSpinnerModal(false);
      } else {
        const data = await response.json();
        setApiResult(data);
        setTimeout(() => {
          setShowSpinnerModal(false);
          setTabIndex(1);
        }, 2000);
      }
    } catch (error) {
      clearInterval(interval);
      setShowSpinnerModal(false);
      alert(`Error processing test script: ${error.message}`);
    }
  };

  return (
    <Box sx={{ width: '100%', marginTop: -7 }}>
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        sx={{
          pl: 0,
          '& .MuiTabs-flexContainer': { justifyContent: 'flex-start' },
          '& .MuiTab-root': {
            minWidth: 120,
            fontWeight: 'bold',
            textTransform: 'none',
            paddingRight: 0,
            pl: 0,
          },
        }}
        TabIndicatorProps={{ style: { display: 'none' } }}
      >
        <Tab
          label={
            <Box display="flex" alignItems="center">
              {/* 1 Input */}
              <Box display="flex" alignItems="center">
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width={24}
                  height={24}
                  borderRadius="50%"
                  bgcolor={apiResult?.report ? "#4caf50" : (tabIndex === 0 ? "#1976d2" : "white")}
                  border="1.5px solid"
                  borderColor={apiResult?.report ? "#4caf50" : (tabIndex === 0 ? "#1976d2" : "black")}
                  mr={1}
                  transition="all 0.2s"
                >
                  {apiResult?.report ? (
                    <CheckCircleIcon sx={{ color: "white", fontSize: 20 }} />
                  ) : (
                    <Typography variant="subtitle2" sx={{ color: tabIndex === 0 ? "white" : "black", fontWeight: 600 }}>1</Typography>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontWeight: tabIndex === 0 ? 700 : 400,
                    color: tabIndex === 0 ? "primary.main" : "black",
                    userSelect: "none",
                    transition: "all 0.2s"
                  }}
                >
                  Input
                </Typography>
              </Box>
              {/* Arrow */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: 4,
                  ml: 1,
                  mr: 1,
                  mb: 0.6,
                }}
              >
                <Typography sx={{ color: "#bdbdbd", fontWeight: 700, fontSize: 44, lineHeight: 1 }}>
                  &rarr;
                </Typography>
              </Box>
            </Box>
          }
          disableRipple
          style={{ minWidth: 0, paddingRight: 0, paddingLeft: 0 }}
        />
        <Tab
          label={
            <Box display="flex" alignItems="center">
              {/* 2 Result */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={24}
                height={24}
                borderRadius="50%"
                bgcolor={tabIndex === 1 ? "#1976d2" : "white"}
                border="1.5px solid"
                borderColor={tabIndex === 1 ? "#1976d2" : "black"}
                mr={1}
                transition="all 0.2s"
              >
                <Typography variant="subtitle2" sx={{ color: tabIndex === 1 ? "white" : "black", fontWeight: 600 }}>2</Typography>
              </Box>
              <Typography
                sx={{
                  fontWeight: tabIndex === 1 ? 700 : 400,
                  color: tabIndex === 1 ? "primary.main" : "black",
                  userSelect: "none",
                  transition: "all 0.2s"
                }}
              >
                Result
              </Typography>
            </Box>
          }
          disableRipple
          style={{ minWidth: 0, paddingLeft: 0 }}
        />
      </Tabs>

      {tabIndex === 0 && (
        <Box sx={{ marginTop: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
            Please upload file for generating Test Data
          </Typography>
          <Button
            variant="contained"
            color="primary"
            component="label"
            sx={{
              backgroundColor: 'white',
              color: 'black',
              border: '1px solid black',
              boxShadow: 'none',
              height: '40px',
            }}
          >
            Upload File
            <input
              type="file"
              hidden
              onChange={handleFileUpload}
            />
          </Button>
          {/* Display the uploaded file name, Special Instruction, and Submit button only after file is uploaded */}
          {testdataFileName && (
            <>
              <Typography
                variant="body2"
                sx={{ marginTop: 1, color: 'green', fontWeight: 'bold' }}
              >
                Uploaded File: {testdataFileName}
              </Typography>
              {/* Special Instruction for Test Data */}
              <Box sx={{ marginTop: 2, width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', marginRight: 1 }}>
                    Special Instruction for Test Data
                  </Typography>
                  <Tooltip title="Generate 50 rows of data" arrow>
                    <InfoIcon sx={{ color: 'gray', cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
                <TextField
                  variant="outlined"
                  size="small"
                  value={specialInstruction}
                  onChange={(event) => setSpecialInstruction(event.target.value)}
                  sx={{ width: '250px' }}
                />
              </Box>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                sx={{
                  marginTop: 2,
                  backgroundColor: 'white',
                  color: 'black',
                  border: '1px solid black',
                  boxShadow: 'none',
                  height: '40px',
                }}
              >
                Submit
              </Button>
            </>
          )}
        </Box>
      )}

      {tabIndex === 1 && isFinalSubmit && (
        <Box sx={{ marginTop: 4 }}>
          <Box
            sx={{
              marginTop: 4,
              padding: '16px',
              backgroundColor: '#f9f9f9',
              borderRadius: '8px',
              border: '1px solid #ccc',
              marginBottom: 2,
            }}
          >
            <Typography variant="h6" sx={{ marginBottom: 2 }}>
              Output
            </Typography>
            {apiResult?.report ? (
              <Typography
                variant="body1"
                sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
              >
                {String(apiResult.report)}
              </Typography>
            ) : (
              <Typography variant="body2">No result available</Typography>
            )}
          </Box>
          {/* Download PDF and Copy Code buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              gap: 2,
              marginTop: 2,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownloadPDF}
              sx={{
                backgroundColor: 'white',
                color: 'black',
                border: '1px solid black',
                boxShadow: 'none',
              }}
              startIcon={<DownloadIcon />}
            >
              Download PDF
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCopyCode}
              sx={{
                backgroundColor: 'white',
                color: 'black',
                border: '1px solid black',
                boxShadow: 'none',
              }}
              startIcon={<ContentCopyIcon />}
            >
              Copy Code
            </Button>
            {/* Download Test Data Button */}
            {apiResult?.links && apiResult.links.length > 0 && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleDownloadTestData}
                sx={{
                  backgroundColor: 'white',
                  color: 'black',
                  border: '1px solid black',
                  boxShadow: 'none',
                }}
                startIcon={<DownloadIcon />}
              >
                Download Test Data
              </Button>
            )}
          </Box>
        </Box>
      )}

      <SpinnerModal
        open={showSpinnerModal}
        progress={progress}
        inProgressMessage="Test Data is being generated..."
        successMessage="Test Data generated successfully!"
        onClose={() => setShowSpinnerModal(false)}
      />
    </Box>
  );
};

export default TestData;